import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, LogIn, ShieldCheck, KeyRound } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, setToken, setUser as setStoredUser } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";

  // step 1 — credentials
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // step 2 — OTP for admin login OR signup email-verify
  const [otpToken, setOtpToken] = useState(null);
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpPurpose, setOtpPurpose] = useState("admin"); // "admin" | "signup"
  const [verifying, setVerifying] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.otp_required) {
        setOtpToken(data.otp_token);
        setOtpSentTo(data.otp_sent_to);
        setOtpPurpose("admin");
        toast.success(`OTP sent to ${data.otp_sent_to}`);
      } else if (data.verification_required) {
        setOtpToken(data.otp_token);
        setOtpSentTo(data.otp_sent_to);
        setOtpPurpose("signup");
        toast.info(`Please verify your email — code sent to ${data.otp_sent_to}`);
      } else {
        setToken(data.token); setStoredUser(data.user); setUser(data.user);
        toast.success(`Welcome back, ${data.user.name}!`);
        nav(data.user.role === "admin" ? "/admin" : next);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const endpoint = otpPurpose === "signup" ? "/auth/register/verify-otp" : "/auth/login/otp";
      const { data } = await api.post(endpoint, { otp_token: otpToken, otp_code: otpCode });
      setToken(data.token); setStoredUser(data.user); setUser(data.user);
      toast.success(`Welcome${otpPurpose === "signup" ? " to Optivize" : " back"}, ${data.user.name}!`);
      nav(data.user.role === "admin" ? "/admin" : next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP verification failed");
    } finally { setVerifying(false); }
  };

  const resend = async () => {
    if (otpPurpose !== "signup") {
      toast.info("Please re-enter your password to resend the OTP.");
      setOtpToken(null);
      return;
    }
    try {
      const { data } = await api.post("/auth/register/resend-otp", { otp_token: otpToken });
      setOtpToken(data.otp_token);
      toast.success(`New code sent to ${data.otp_sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend code");
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16">
      <div className="card p-8 max-w-md w-full shine-border">
        <div className="text-center mb-6">
          <div className="tag mb-3">{otpToken ? "Verify OTP" : "Sign In"}</div>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>
            {otpToken ? "Two-step verification" : "Welcome back"}
          </h1>
          {otpToken && (
            <p className="text-slate-400 text-sm mt-2">
              We sent a 6-digit code to <strong className="text-cyan-300">{otpSentTo}</strong>.
              Enter it below to complete sign-in.
            </p>
          )}
        </div>

        {!otpToken ? (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div><label className="label">Email</label><input type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="input w-full" data-testid="login-email"/></div>
            <div><label className="label">Password</label><input type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="input w-full" data-testid="login-password"/></div>
            <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="login-submit">
              {loading ? <Loader2 size={16} className="animate-spin"/> : <LogIn size={16}/>} Sign In
            </button>
            <div className="text-center text-sm text-gray-400">
              New to Optivize? <Link to="/signup" className="text-cyan-400">Create an account</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-4" data-testid="otp-form">
            <div>
              <label className="label inline-flex items-center gap-1.5"><KeyRound size={12}/> One-time password</label>
              <input
                value={otpCode}
                onChange={e=>setOtpCode(e.target.value.replace(/\s/g,""))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="• • • • • •"
                className="input w-full text-center text-2xl tracking-[12px] font-mono"
                maxLength={15}
                data-testid="otp-code"
                required
              />
            </div>
            <button type="submit" disabled={verifying} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="otp-verify-btn">
              {verifying ? <Loader2 size={16} className="animate-spin"/> : <ShieldCheck size={16}/>}
              Verify & continue
            </button>
            <button type="button" onClick={()=>{setOtpToken(null); setOtpCode("");}} className="text-sm text-slate-400 hover:text-slate-200 w-full text-center" data-testid="otp-back-btn">
              ← Use a different account
            </button>
            <button type="button" onClick={resend} className="text-xs text-cyan-400 hover:text-cyan-300 w-full text-center" data-testid="otp-resend-btn">
              Resend code
            </button>
            <p className="text-xs text-slate-500 text-center">
              Didn't get the email? Check spam, or contact support at sales@optivize.digital.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
